/**
 * @copyright (C) 2025, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Charyung Kim (crkim@rsupport.com)
 *
 * @description
 *
 * @created at Fri Jan 10 2025
 **/

import { drawArrow, drawLine, drawFreeLine, drawRect, drawCircle, drawEllipse } from "@rstudio/canvas-v2";

import { DrawingManager } from "./drawingManager";
import { makeExCoordinate, drawEmojiObject, drawEmojiPointer, drawImageRect, drawText, loadEmojiIcons } from "./utils";
import Presets from "../presets";
import { DRAWING_STATE, MODE, OBJECT_TYPE, PEN_TYPE } from "./types/enum";

import sampleImage from "../assets/images/sample2.png";
const BASE_HEIGHT = 1080;

// 캔버스 엘리먼트를 사용한 그리기 객체
export class DrawingController implements IWhiteboardProps {
	id: string = "";
	alpha: number = 1;
	objectStyle: IWhiteboardDrawingObject = {
		penType: PEN_TYPE.PEN,
		strokeColor: Presets.penColors[0],
		fillColor: "transparent",
		thickness: Presets.thicknessRange.default,
		alpha: 1,
		objectType: OBJECT_TYPE.FREE_LINE,
		emojiSourceIndex: 0
	};
	emojiStyle: IWhiteboardEmojiObject = {
		icons: loadEmojiIcons(Presets.emojiIcons),
		size: { width: 50, height: 50 },
		alpha: 1
	};
	attributes: IAttributes = {
		markerThickness: 24,
		markerOpacity: 0.6,
		arrowSize: 5
	};
	historyMax: number = 11;
	mode: MODE = MODE.DRAWING;
	useCacheImage = true;

	ctx?: CanvasRenderingContext2D;
	drawingState = DRAWING_STATE.END;
	object?: IWhiteboardDrawingObject | null;
	pointer?: coordinateEx;
	manager: DrawingManager;

	// creates an instance of whiteBoard
	constructor(id: string, manager: DrawingManager) {
		this.id = id;
		this.manager = manager;
		this.ctx = manager.getOutputCtx();
	}

	// 지원 동작(그리기, 포인팅) 모드 선택
	public changeMode(mode: MODE = MODE.DRAWING): void {
		this.mode = mode;
	}

	// Object 스타일 변경
	public setObjectStyle(style: IWhiteboardDrawingObject) {
		this.objectStyle = { ...this.objectStyle, ...style };
		this.alpha = style.alpha || 1;
	}

	// network drawing - redraw
	public redrawObject(ctx: CanvasRenderingContext2D) {
		if (this.object) this.drawObject(ctx, this.object);
	}

	// network drawing - set image source
	public setImageSrc(filePath: string) {
		const image = new Image();
		image.src = filePath == "use-sample" ? sampleImage : filePath;
		image.onload = () => (this.attributes.imageSrc = image);
	}

	// 출력
	public draw() {
		const ctx = this.ctx;
		if (ctx) {
			ctx.save();
			// draw object
			if (this.object && this.object.objectType !== OBJECT_TYPE.EMPTY) this.drawObject(ctx, this.object);
			// draw emoji pointer
			if (this.pointer && this.objectStyle.objectType === OBJECT_TYPE.EMOJI)
				drawEmojiPointer(ctx, this.pointer, this.emojiStyle, this.objectStyle.emojiSourceIndex);
			ctx.restore();
		}
	}

	// 그리기 시작 (mouse down)
	public drawingStart(point: Vector2, props?: IWhiteboardDrawingObject, force: boolean = false) {
		if (props && props.objectType === OBJECT_TYPE.IMAGE && props.imageSourcePath) {
			this.setImageSrc(props.imageSourcePath);
		}
		if (force || this.mode === MODE.DRAWING) {
			this.drawingState = DRAWING_STATE.START;
			const object: IWhiteboardDrawingObject = {
				objectType: OBJECT_TYPE.FREE_LINE,
				...this.objectStyle,
				points: [makeExCoordinate(point)],
				...props
			};
			if (object.penType === PEN_TYPE.ERASER) {
				object.alpha = 1;
			} else if (object.penType === PEN_TYPE.MARKER) {
				const attributes = this.attributes;
				if (attributes.markerOpacity) object.alpha = attributes.markerOpacity;
				if (attributes.markerThickness) object.thickness = attributes.markerThickness + object.thickness;
			}
			this.alpha = object.alpha || 1;
			this.object = object;
			this.object.redrawObject = (ctx: CanvasRenderingContext2D) => this.redrawObject(ctx);
			this.drawingTo([point], force);
		}
	}

	// 그리기 진행 (mouse drag)
	public drawingTo(points?: Vector2[], force: boolean = false) {
		if (force || this.mode === MODE.DRAWING) {
			this.drawingState = DRAWING_STATE.DRAWING;
			const object = this.object;
			if (object && object.objectType !== OBJECT_TYPE.EMPTY) {
				if (object.points && points) {
					for (const point of points) {
						const pointEx = makeExCoordinate(point);
						if (object.objectType === OBJECT_TYPE.FREE_LINE) {
							object.points.push(pointEx);
						} else {
							if (object.points.length === 1) object.points.push(pointEx);
							else object.points[1] = pointEx;
						}
					}
				}
			}
		}
	}

	// 그리기 종료 (mouse up)
	public drawingEnd(point?: Vector2, force: boolean = false) {
		if (force || this.mode === MODE.DRAWING) {
			this.drawingState = DRAWING_STATE.END;
			const object = this.object;
			if (object && object.objectType !== OBJECT_TYPE.EMPTY) {
				if (object.points && point) {
					object.points.push(makeExCoordinate(point));
					const attrProps = {
						arrowSize: this.attributes.arrowSize,
						emojiSize: this.emojiStyle.size,
						imageSrc: this.attributes.imageSrc
					};
					this.manager?.addOnList(object, attrProps);
				}
			}
		}
	}

	// 그리기 객체 출력
	private drawObject(ctx: CanvasRenderingContext2D, object: IWhiteboardDrawingObject) {
		// object에 image가 만들어지지 않았다면 canvas에 object그리기
		if (object?.points && !object.image) {
			ctx.save();
			const points = object.points;
			this.updateDrawingStyle(ctx, object);
			if (object.objectType === OBJECT_TYPE.FREE_LINE) {
				drawFreeLine(ctx, points);
			} else if (object.objectType === OBJECT_TYPE.CIRCLE) {
				drawCircle(ctx, points);
			} else if (object.objectType === OBJECT_TYPE.LINE) {
				drawLine(ctx, points);
			} else if (object.objectType === OBJECT_TYPE.ARROW) {
				drawArrow(ctx, points, this.attributes.arrowSize);
			} else if (object.objectType === OBJECT_TYPE.RECT) {
				drawRect(ctx, points);
			} else if (object.objectType === OBJECT_TYPE.ELLIPSE) {
				drawEllipse(ctx, points);
			} else if (object.objectType === OBJECT_TYPE.EMOJI) {
				drawEmojiObject(ctx, points[0], this.emojiStyle, object.emojiSourceIndex);
			} else if (object.objectType === OBJECT_TYPE.IMAGE) {
				if (this.attributes.imageSrc) drawImageRect(ctx, points, this.attributes.imageSrc);
			} else if (object.objectType === OBJECT_TYPE.TEXT) {
				if (this.attributes.textSrc) drawText(ctx, points, this.attributes.textSrc, object.strokeColor);
			}
			ctx.restore();
		}
	}

	// object의 내용으로 캔버스 컨텍스트 스타일 설정
	private updateDrawingStyle(ctx: CanvasRenderingContext2D, object: IWhiteboardDrawingObject) {
		const yRatio = this.manager.getResolution().height / BASE_HEIGHT;
		ctx.lineJoin = "round";
		ctx.lineCap = object.penType === PEN_TYPE.MARKER ? "butt" : "round";
		ctx.lineWidth = object.thickness * yRatio;
		ctx.strokeStyle = object.strokeColor;
		ctx.fillStyle = object.fillColor;
		ctx.globalAlpha = object.penType === PEN_TYPE.ERASER ? object.alpha : this.alpha;
		ctx.globalCompositeOperation = object.penType === PEN_TYPE.ERASER ? "destination-out" : "source-over";
	}
}
